import type { CoreEnv } from '@eventbrite/context-gen';
import { Button } from '@eventbrite/eds-button';
import { DiscoveryEvent } from '@eventbrite/event-renderer';
import { RenderGlobalNavOrUndefined } from '@eventbrite/global-nav';
import {
    getLocaleOverride,
    gettext,
    setLanguage,
    setup as setupI18n,
} from '@eventbrite/i18n';
import { SiteStructure } from '@eventbrite/site-structure';
import { logEvent } from '@eventbrite/statsig';
import {
    UrgencySignalsProps,
    UrgencySignalsProvider,
} from '@eventbrite/urgency-signals';
import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { css, cx } from 'emotion';
import React, { useState } from 'react';
import { renderToString } from 'react-dom/server';
import { App } from './app';
import { CreatorBanner } from './components/CreatorBanner/CreatorBanner';
import { ActionsPanel } from './components/EventDetails/ActionsPanel/ActionsPanel';
import { ConversionBarContainer } from './components/EventDetails/ConversionBarContainer/ConversionBarContainer';
import { CreatorCollections } from './components/EventDetails/CreatorCollections/CreatorCollections';
import type { EventDescriptionProps } from './components/EventDetails/EventDescription/EventDescription';
import {
    AboutThisEvent,
    OldDescriptionBeforeStructuredContent,
} from './components/EventDetails/EventDescription/EventDescription';
import {
    EventDetails,
    EventDetailsAside,
    EventDetailsMain,
    EventDetailsMainInner,
    EventDetailsWrapper,
} from './components/EventDetails/EventDetails';
import { EventHero } from './components/EventDetails/EventHero';
import { MusicEventsInfo } from './components/EventDetails/MusicEventsInfo/MusicEventsInfo';
import { Performers } from './components/EventDetails/Performers/Performers';
import { RefundPolicy } from './components/EventDetails/RefundPolicy/RefundPolicy';
import { SalesEndedMessage } from './components/EventDetails/SalesEndedMessage/SalesEndedMessage';
import { StartDate } from './components/EventDetails/StartDate/StartDate';
import { Summary } from './components/EventDetails/Summary/Summary';
import { WhenAndWhere } from './components/EventDetails/WhenAndWhere/WhenAndWhere';
import { EventManagerBanner } from './components/EventManagerBanner/EventManagerBanner';
import { EnhancedExpiredEventsBadge } from './components/ExpiredEvents/EnhancedExpiredEventsBadge';
import { EnhancedExpiredEventsList } from './components/ExpiredEvents/EnhancedExpiredEventsList';
import { ExpiredEventsPage } from './components/ExpiredEvents/ExpiredEventsPage';
import type { FooterLinks } from './components/Footer/Footer';
import { Footer } from './components/Footer/Footer';
import { HighlightsProps } from './components/Highlights';
import { useGetStatsigEnrollment } from './components/hooks/useGetStatsigVariant';
import type { NotificationBarSsrProps } from './components/NotificationBar/NotificationBarSsr';
import { NotificationBarSsr } from './components/NotificationBar/NotificationBarSsr';
import type { OrganizerPanelLayoutProps } from './components/OrganizerPanel/OrganizerPanelLayout';
import { OrganizerPanelLayout } from './components/OrganizerPanel/OrganizerPanelLayout';
import { SeasonalAdBanner } from './components/SeasonalAdBanner/SeasonalAdBanner';
import {
    getShareComponentProps,
    ShareProps,
} from './components/ShareEvent/ShareEventRoot';
import type { Tag } from './components/Tags/Tags';
import { Tags } from './components/Tags/Tags';
import { UrgencySignalsWrapper } from './components/UrgencySignalsWrapper/UrgencySignalsWrapper';
import {
    FollowButtonGDPRModal,
    FollowButtonLoginModal,
    FollowButtonProvider,
} from './components/_shared/FollowButton';
import { SimplifiedOrganizerInfo } from './components/_shared/OrganizerInfo/SimplifiedOrganizerInfo';
import {
    LISTING_HEAP_PAGE_AREA_IDS,
    LISTING_SEASONAL_AD_BANNER,
    SALES_ENDED_LISTINGS_PAGE_EXPERIMENT,
    SEASONAL_BANNER_CONTENT,
    STATSIG_EVENT_NAMES,
} from './constants';
import { TrackingContext } from './context';
import { EventBasicInformationContext } from './contexts/EventContext';
import { getTrackingContextFrom } from './getTrackingContext';
import { isEarlyBirdShownAsDiscount } from './statsig';
import type {
    AppProps,
    CheckoutWidgetProps,
    Event,
    EventDetailsProps,
    EventHeroScopedProps,
    EventMapScopedProps,
    FollowOrganizerScopedProps,
    LegacyConversionBarProps,
    Organizer,
    RemindMeScopedProps,
    Request,
    Settings,
    User,
} from './types';
import { AffiliateCodes, SALES_STATUS } from './types';

const titleCustomStyle = (hideStartDate: boolean) => css`
    ${hideStartDate &&
    `
    @media (max-width: 960px) {
        width: 80%;
    }
    `}
`;
const FEATURE_FLAGS = {
    launchCreatorCreditsSectionAttendee: true,
};

export interface ServerAppProps {
    checkoutWidget: CheckoutWidgetProps;
    conversionBar: LegacyConversionBarProps;
    user: User;
    event: Event;
    env: CoreEnv;
    request: Request;
    footerLinks: FooterLinks;
    eventDescription: EventDescriptionProps;
    eventDetails: EventDetailsProps;
    organizer: Organizer;
    tags: Array<Tag>;
    settings: Settings;
    organizerPanelLayout: OrganizerPanelLayoutProps;
    eventMapLayout: EventMapScopedProps;
    eventHero: EventHeroScopedProps;
    notificationBar: NotificationBarSsrProps;
    expiredEventsPage: {
        shareComponentProps: ShareProps;
        relatedEvents: DiscoveryEvent[];
    };
    urgencySignals: UrgencySignalsProps;
    highlights: HighlightsProps;
    followOrganizer: FollowOrganizerScopedProps;
    contactOrganizer: { captchaKey: string };
    remindMe: RemindMeScopedProps;
    gaCategory: string;
    gaSettings: any;
    salesStatus: string;
}

export const ServerApp = ({
    checkoutWidget,
    conversionBar,
    user,
    event,
    env,
    request,
    footerLinks,
    eventDescription,
    eventDetails,
    organizer,
    tags,
    settings,
    organizerPanelLayout,
    eventMapLayout,
    eventHero,
    notificationBar = {
        message: null,
        type: null,
    },
    expiredEventsPage,
    urgencySignals,
    highlights,
    followOrganizer,
    contactOrganizer: { captchaKey },
    remindMe,
    gaCategory,
    gaSettings,
    salesStatus,
}: ServerAppProps) => {
    const {
        ageRestriction,
        discount,
        hasCroppedLogo,
        hasPresenterOrAgeRestriction,
        organizerSalesEndedMessage,
        presenter,
        salesEndedMessage,
        shouldShowSalesEndedMessage,
        startDateTime,
        isOnlineEvent,
        hideStartDate,
        isSeriesParent,
    } = event;

    const {
        hasStructuredContent,
        structuredContent,
        summary,
        legacyDescription,
    } = eventDescription;

    const {
        id: organizerId,
        name: organizerName,
        profilePicture: organizerAvatar,
        url: organizerUrl,
        description: organizerDescription,
        orgFacebook: organizerFacebook,
        orgTwitter: organizerTwitter,
        orgWebsite: organizerWebsite,
    } = organizer;

    const {
        featureFlags: {
            enableOpenDiscountsOnListings,
            enableIaGlobalNav = false,
            launchLinkToCreatorCollectionsOnListing = false,
        } = {},
    } = settings;

    const {
        localeInfo: { locale },
    } = env;

    const { performers } = eventDetails;
    const hasPerformers = performers !== null;

    const isSalesEnded = salesStatus === SALES_STATUS.SALES_ENDED;
    const isSalesEndedListingExperimentEnabled =
        useGetStatsigEnrollment({
            name: SALES_ENDED_LISTINGS_PAGE_EXPERIMENT.experimentName,
            paramName: SALES_ENDED_LISTINGS_PAGE_EXPERIMENT.param,
            defaultValue: SALES_ENDED_LISTINGS_PAGE_EXPERIMENT.defaultValue,
            enabled: isSalesEnded,
        }).variant === true;

    const isSeasonalAdBannerShown =
        useGetStatsigEnrollment({
            name: LISTING_SEASONAL_AD_BANNER.experimentName,
            paramName: LISTING_SEASONAL_AD_BANNER.param,
            defaultValue: LISTING_SEASONAL_AD_BANNER.defaultValue,
            enabled: true,
        }).variant === true;

    const eventBasicInfo = {
        id: event.id,
        name: event.name,
        summary: summary,
        place: {
            isOnline: isOnlineEvent,
            latitude: eventMapLayout.location.latitude,
            longitude: eventMapLayout.location.longitude,
            placeId: eventDetails?.location?.localityPlaceId,
        },
    };

    const isEarlyBirdExperimentVariant =
        urgencySignals?.categories?.earlyBird && isEarlyBirdShownAsDiscount();
    const urgencySignalsUpdated: UrgencySignalsProps = {
        messages: { ...urgencySignals?.messages },
        categories: { ...urgencySignals?.categories },
    };
    if (
        isEarlyBirdExperimentVariant &&
        urgencySignalsUpdated &&
        urgencySignalsUpdated.categories
    ) {
        urgencySignalsUpdated.categories.earlyBird = false;
    }

    const LiveEventMainSection = ({
        onViewAllDetailsClick,
        viewAllEventDetails,
        handleExploreSimilareEventsButtonClick,
        scrollToRelatedEvents,
    }: {
        onViewAllDetailsClick: Function;
        viewAllEventDetails: boolean;
        handleExploreSimilareEventsButtonClick: Function;
        scrollToRelatedEvents: boolean;
    }) => {
        const viewEventDetailsWrapperClasses = classNames({
            'event-listing__view-more-details--hide':
                isSalesEndedListingExperimentEnabled && !viewAllEventDetails,
            'event-listing__view-more-details--show':
                isSalesEndedListingExperimentEnabled && viewAllEventDetails,
        });

        return (
            <div
                className={`event-listing ${
                    hasCroppedLogo ? 'event-listing--has-image' : ''
                }`}
                role="article"
                data-testid="live-event-main-section"
                data-show-seasonal-ad-banner={isSeasonalAdBannerShown}
            >
                {!isSalesEndedListingExperimentEnabled &&
                    notificationBar.message && (
                        <NotificationBarSsr {...notificationBar} />
                    )}
                {user.canManageEvent &&
                    !settings.featureFlags
                        ?.launchMobilePreviewInEventCreationFlow && (
                        <CreatorBanner />
                    )}
                <EventDetails
                    hasHeroSection={hasCroppedLogo || !!eventHero.featuredVideo}
                >
                    <EventHero
                        mainBodyBackground={eventHero.mainBodyBackground}
                        hasCroppedLogo={hasCroppedLogo}
                        items={eventHero.items}
                        eventName={event.name}
                        featuredVideo={eventHero.featuredVideo}
                    />

                    <UrgencySignalsProvider
                        urgencySignals={urgencySignalsUpdated}
                    >
                        <EventDetailsWrapper>
                            <ActionsPanel
                                isShareable={conversionBar.isShareable}
                                isEnded={conversionBar.isEnded}
                                isSalesEndedListingExperimentEnabled={
                                    isSalesEndedListingExperimentEnabled
                                }
                            />

                            <EventDetailsMain>
                                <EventDetailsMainInner>
                                    {isSalesEndedListingExperimentEnabled ? (
                                        <EnhancedExpiredEventsBadge
                                            isEventDetailsPage={true}
                                            salesMessage={
                                                event.salesStatus.message
                                            }
                                        />
                                    ) : null}

                                    <SalesEndedMessage
                                        shouldShowSalesEndedMessage={
                                            shouldShowSalesEndedMessage
                                        }
                                        organizerSalesEndedMessage={
                                            organizerSalesEndedMessage
                                        }
                                        salesEndedMessage={salesEndedMessage}
                                    />

                                    <UrgencySignalsWrapper />
                                    <StartDate
                                        startDateTime={startDateTime}
                                        isOnlineEvent={isOnlineEvent}
                                        localizedDateTimeStart={event.start}
                                        childEvents={event.childEvents}
                                        hideStartDate={hideStartDate}
                                        locale={locale}
                                        isSeriesParent={isSeriesParent}
                                    />
                                    <meta content={event.name} />
                                    <h1
                                        className={cx(
                                            'event-title',
                                            titleCustomStyle(hideStartDate),
                                        )}
                                    >
                                        {event.name}
                                    </h1>
                                    {hasPresenterOrAgeRestriction ? (
                                        <MusicEventsInfo
                                            ageRestriction={ageRestriction}
                                            presenter={presenter}
                                        />
                                    ) : null}
                                    <CreatorCollections />
                                    <Summary summary={summary} />
                                    {organizer.displayOrganizationName && (
                                        <SimplifiedOrganizerInfo
                                            organizerName={organizerName}
                                            organizerAvatar={organizerAvatar}
                                            organizerId={organizerId}
                                            pageArea={
                                                LISTING_HEAP_PAGE_AREA_IDS.HERO_CONTENT
                                            }
                                        />
                                    )}
                                </EventDetailsMainInner>

                                {isSalesEndedListingExperimentEnabled &&
                                !viewAllEventDetails ? (
                                    <EventDetailsMainInner>
                                        <div className="event-listing__view-more-details-btn-container">
                                            <Button
                                                data-testid="view-event-details-button"
                                                style="neutral"
                                                type="button"
                                                onClick={onViewAllDetailsClick}
                                            >
                                                View all event details
                                            </Button>
                                        </div>
                                    </EventDetailsMainInner>
                                ) : null}
                                <section
                                    className={viewEventDetailsWrapperClasses}
                                    data-testid="event-details-section"
                                >
                                    <WhenAndWhere
                                        dates={{
                                            ...eventDetails.dates,
                                            event: {
                                                id: event.id,
                                                start: event.start,
                                                end: event.end,
                                                hideEndDate: event.hideEndDate,
                                                hideStartDate:
                                                    event.hideStartDate,
                                                isOnlineEvent:
                                                    event.isOnlineEvent,
                                                childEvents: event.childEvents,
                                                listingDisplayFlags: {
                                                    shouldShowTimezone: true,
                                                },
                                            },
                                            locale,
                                            isSeriesParent:
                                                event.isSeriesParent,
                                        }}
                                        location={{
                                            ...eventDetails.location,
                                            isOnlineEvent: event.isOnlineEvent,
                                            onlineEventUrl:
                                                eventDetails.onlineEvent?.url,
                                        }}
                                        map={{
                                            location: eventMapLayout.location,
                                            gMapsApiKey:
                                                eventMapLayout.gMapsApiKey,
                                            shouldDisplayMap:
                                                eventMapLayout.shouldDisplayMap,
                                        }}
                                        isSeriesParent={event.isSeriesParent}
                                    />
                                    <EventDetailsMainInner>
                                        {hasPerformers && (
                                            <Performers
                                                headliners={
                                                    eventDetails.performers
                                                        .headliners
                                                }
                                                supporters={
                                                    eventDetails.performers
                                                        .supporters
                                                }
                                            />
                                        )}
                                        <RefundPolicy
                                            isEnableRetainFeeActive={
                                                eventDetails.refund
                                                    .isEnableRetainFeeActive
                                            }
                                            isPaidEvent={
                                                eventDetails.refund.isPaidEvent
                                            }
                                            isRefundRequestAllowed={
                                                eventDetails.refund
                                                    .isRefundRequestAllowed
                                            }
                                            refundPolicyDescription={
                                                eventDetails.refund
                                                    .refundPolicyDescription
                                            }
                                            refundPolicyIsNoRefunds={
                                                eventDetails.refund
                                                    .refundPolicyIsNoRefunds
                                            }
                                        />
                                        {hasStructuredContent ||
                                        !legacyDescription ? (
                                            <AboutThisEvent
                                                hasAutogeneratedContent={
                                                    structuredContent.hasAutogeneratedContent
                                                }
                                                hasStructuredContent={
                                                    hasStructuredContent
                                                }
                                                admat={structuredContent.admat}
                                                modules={
                                                    structuredContent.modules
                                                }
                                                widgets={
                                                    structuredContent.widgets
                                                }
                                                highlights={highlights}
                                            />
                                        ) : (
                                            <OldDescriptionBeforeStructuredContent
                                                legacyDescription={
                                                    legacyDescription
                                                }
                                                highlights={highlights}
                                            />
                                        )}
                                        {isSeasonalAdBannerShown ? (
                                            <SeasonalAdBanner
                                                {...SEASONAL_BANNER_CONTENT[
                                                    'HALLOWEEN'
                                                ]}
                                            />
                                        ) : null}
                                        {!isSalesEndedListingExperimentEnabled ? (
                                            <Tags tags={tags} />
                                        ) : null}
                                        <OrganizerPanelLayout
                                            {...{
                                                ...organizerPanelLayout,
                                                captchaKey: captchaKey,
                                                organizerAvatar:
                                                    organizerAvatar,
                                                organizerDescription:
                                                    organizerDescription,
                                                organizerId: organizerId,
                                                organizerName: organizerName,
                                                organizerUrl: organizerUrl,
                                                organizerFacebook:
                                                    organizerFacebook,
                                                organizerTwitter:
                                                    organizerTwitter,
                                                organizerWebsite:
                                                    organizerWebsite,
                                                isAuthenticated:
                                                    user.isAuthenticated,
                                                eventName: event.name,
                                                gaCategory: gaCategory,
                                                isSalesEndedListingExperimentEnabled,
                                            }}
                                        />
                                        {isSalesEndedListingExperimentEnabled ? (
                                            <Tags
                                                tags={tags}
                                                customHeading="Related to this event"
                                            />
                                        ) : null}
                                    </EventDetailsMainInner>
                                </section>
                                {isSalesEndedListingExperimentEnabled ? (
                                    <EventBasicInformationContext.Provider
                                        value={eventBasicInfo}
                                    >
                                        <TrackingContext.Provider
                                            value={getTrackingContextFrom({
                                                user,
                                                event,
                                                settings,
                                                env,
                                                request,
                                                components: { eventDetails },
                                            })}
                                        >
                                            <EnhancedExpiredEventsList
                                                {...{
                                                    isAuthenticated:
                                                        user.isAuthenticated,
                                                    locale,
                                                    affCode:
                                                        AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED,
                                                    organizer,
                                                    launchLinkToCreatorCollectionsOnListing,
                                                    isExpiredEvent:
                                                        event.isExpiredEvent,
                                                    forceTwoColumnLayout: true,
                                                    isSalesEndedListingExperimentEnabled,
                                                    scrollToRelatedEvents,
                                                    isSalesEnded,
                                                }}
                                            />
                                        </TrackingContext.Provider>
                                    </EventBasicInformationContext.Provider>
                                ) : null}
                            </EventDetailsMain>
                            <EventDetailsAside>
                                <ConversionBarContainer
                                    {...{
                                        ...conversionBar,
                                        ...checkoutWidget,
                                        externalTickets: {
                                            url: conversionBar.externalTicketsUrl,
                                            text: conversionBar.externalTicketsButtonText,
                                            available:
                                                conversionBar.externalTicketsAreAvailable,
                                            shouldShowExternalTicketingProvider:
                                                conversionBar.shouldShowExternalTicketingProvider,
                                            externalTicketingProviderLabelText:
                                                conversionBar.externalTicketingProviderLabelText,
                                        },
                                        childEvents: event.childEvents,
                                        isSeriesParent: event.isSeriesParent,
                                        isExpiredEvent: event.isExpiredEvent,
                                        isPreview: event.isPreview,
                                        eventName: event.name,
                                        ticketsInfo: event.ticketsInfo,
                                        compactCheckoutDisqualifications:
                                            event.compactCheckoutDisqualifications,
                                        salesStatus:
                                            event.salesStatus.sales_status,
                                        eventUrl: event.url,
                                        gaCategory,
                                        gaSettings,
                                        isAuthenticated: user.isAuthenticated,
                                        isGDPRCountry:
                                            followOrganizer.isGDPRCountry,
                                        isRemindedEvent:
                                            remindMe?.isRemindedEvent,
                                        organizerId,
                                        organizerData: {},
                                        organizers: {},
                                        userId: user.id,
                                        organizerName: organizer.name,
                                        organizerAvatar:
                                            organizer.profilePicture,
                                        discount: discount,
                                        enableOpenDiscountsOnListings:
                                            enableOpenDiscountsOnListings,
                                        isSalesEndedListingExperimentEnabled,
                                        handleExploreSimilareEventsButtonClick,
                                        hasEarlyBird:
                                            urgencySignals?.categories
                                                .earlyBird,
                                    }}
                                />
                            </EventDetailsAside>
                        </EventDetailsWrapper>
                    </UrgencySignalsProvider>
                </EventDetails>

                {shouldShowRelatedEventsLive &&
                    !isSalesEndedListingExperimentEnabled && (
                        <div id="listings-root__related-events"></div>
                    )}
                <EventManagerBanner canManage={user.canManageEvent || false} />
            </div>
        );
    };
    const MainContent = () => {
        const [shouldHideMainBody, setHideMainBody] = useState(
            !event.isPreview && event.isExpiredEvent,
        );
        const [viewAllEventDetails, setViewAllEventDetails] = useState(false);
        const [scrollToRelatedEvents, setScrollToRelatedEvents] =
            useState(false);

        const showEventMainSection = () => {
            setHideMainBody(false);
        };

        const showAllEventDetails = () => {
            setHideMainBody(false);
            setViewAllEventDetails(true);
            logEvent(STATSIG_EVENT_NAMES.LISTING_ALL_EVENT_DETAILS_VIEW);
        };

        const handleExploreSimilareEventsButtonClick = () => {
            setScrollToRelatedEvents(true);

            // immediately reset
            setTimeout(() => {
                setScrollToRelatedEvents(false);
            });
        };

        return (
            <>
                <EventBasicInformationContext.Provider value={eventBasicInfo}>
                    {!shouldHideMainBody && (
                        <LiveEventMainSection
                            onViewAllDetailsClick={showAllEventDetails}
                            viewAllEventDetails={viewAllEventDetails}
                            handleExploreSimilareEventsButtonClick={
                                handleExploreSimilareEventsButtonClick
                            }
                            scrollToRelatedEvents={scrollToRelatedEvents}
                        />
                    )}
                    {shouldHideMainBody && (
                        <TrackingContext.Provider
                            value={getTrackingContextFrom({
                                user,
                                event,
                                settings,
                                env,
                                request,
                                components: { eventDetails },
                            })}
                        >
                            <ExpiredEventsPage
                                relatedEvents={expiredEventsPage.relatedEvents}
                                eventName={event.name}
                                eventLocation={eventMapLayout.location}
                                organizer={{
                                    name: organizerName,
                                    profilePicture: organizerAvatar,
                                    id: organizerId,
                                }}
                                isAuthenticated={user.isAuthenticated}
                                shareComponentProps={
                                    expiredEventsPage.shareComponentProps
                                }
                                onViewDetailsClick={showEventMainSection}
                                isSalesEndedListingExperimentEnabled={
                                    isSalesEndedListingExperimentEnabled
                                }
                                locale={locale}
                                isExpiredEvent={event.isExpiredEvent}
                                launchLinkToCreatorCollectionsOnListing={
                                    launchLinkToCreatorCollectionsOnListing
                                }
                                scrollToRelatedEvents={scrollToRelatedEvents}
                                isSalesEnded={isSalesEnded}
                                salesMessage={event.salesStatus.message}
                            />
                        </TrackingContext.Provider>
                    )}
                </EventBasicInformationContext.Provider>
            </>
        );
    };

    const shouldShowRelatedEventsLive = event.isLive;

    const cleanEsiCachePaths = (env: CoreEnv) => {
        const esiPath = user.isAuthenticated
            ? /%2Fesi_nocache/g
            : /%2Fesi_cache/g;
        const removeEsiPath = (string: string): string =>
            string ? string.replace(esiPath, '') : '';

        env.loginUrl = removeEsiPath(env.loginUrl);
        env.logoutUrl = removeEsiPath(env.logoutUrl);
        env.signinUrl = removeEsiPath(env.signinUrl);
        env.signupUrl = removeEsiPath(env.signupUrl);
    };

    cleanEsiCachePaths(env);

    const siteWrapperClasses = classNames({
        'conversion-bar-placeholder': !event.isExpiredEvent,
    });

    return (
        <Sentry.ErrorBoundary
            fallback={<>{gettext('Error on Listings SSR')}</>}
        >
            <div className={siteWrapperClasses}>
                <SiteStructure
                    env={env}
                    request={request}
                    user={user}
                    preview={event.isPreview}
                    featureFlags={FEATURE_FLAGS}
                    fullScreenMain={true}
                    globalHeader={RenderGlobalNavOrUndefined({
                        config: { env, user },
                        featureFlag: enableIaGlobalNav,
                    })}
                    footer={
                        <Footer {...footerLinks} serverUrl={env.serverUrl} />
                    }
                    removeOverflowSettings={true}
                    focusDrawerOptions={{ content: '', alwaysInclude: false }}
                >
                    <FollowButtonProvider
                        organizerId={organizerId}
                        userId={user.id}
                        isGDPRCountry={followOrganizer.isGDPRCountry}
                        isAuthenticated={user.isAuthenticated}
                    >
                        <MainContent />
                        <FollowButtonGDPRModal />
                        <FollowButtonLoginModal
                            organizerId={organizerId}
                            organizerName={organizerName}
                            organizerAvatar={organizerAvatar}
                        />
                    </FollowButtonProvider>
                </SiteStructure>
            </div>
        </Sentry.ErrorBoundary>
    );
};

export const render = (props: AppProps) => {
    const locale = getLocaleOverride() || props.env.localeInfo.locale;
    setupI18n(require.context('./i18n/translations', true, /\.json$/));
    setLanguage(locale);

    return renderToString(
        <ServerApp
            user={props.user}
            event={{ ...props.event }}
            request={props.request}
            env={props.env}
            footerLinks={props.footerLinks}
            eventDescription={{ ...props.components.eventDescription }}
            eventDetails={{ ...props.components.eventDetails }}
            tags={props.components.tags}
            conversionBar={props.components.conversionBar}
            checkoutWidget={props.components.checkoutWidget}
            settings={props.settings}
            organizerPanelLayout={props.components.organizerPanel}
            eventMapLayout={props.components.eventMap}
            eventHero={props.components.eventHero}
            organizer={props.organizer}
            notificationBar={props.components.notificationBar}
            expiredEventsPage={{
                ...props.components.relatedEventsExpiredPage,
                shareComponentProps: getShareComponentProps(props),
            }}
            urgencySignals={props.urgencySignals}
            highlights={props.highlights}
            gaCategory={props.gaSettings.category}
            gaSettings={props.gaSettings}
            followOrganizer={props.components.followOrganizer}
            contactOrganizer={props.components.contactOrganizer}
            remindMe={props.components.remindMe}
            salesStatus={props.salesStatus}
        />,
    );
};

export default App;
